/* eslint-disable @typescript-eslint/no-misused-promises */
import axios, { AxiosResponse } from 'axios';
import { Products } from '@lucid/theme';

import { LucidBootstrap } from '../types/api';

export interface RegisterUserData {
  discoveryPage?: boolean;
  /** The users email address. */
  email: string;
  finalizeNow: boolean;
  /** The users name. */
  name?: string;
  /** The users password. */
  password?: string;
  seenTos: boolean;
}

export interface RegisterUserProps {
  checkForService?: boolean;
  data: RegisterUserData;
  urlLangPrefix: string;
  product: Products;
  productUrl: string;
}

export enum RegistrationStatus {
  Success,
  AlreadyRegistered,
}

export interface RegisterUserApiResponse {
  redirectUrl: string;
}

export interface RegisterUserResponse {
  isEnterpriseUser: boolean;
  redirectUrl: string;
  response: AxiosResponse<RegisterUserApiResponse>;
  registrationStatus: RegistrationStatus;
  status?: number;
  errorMessage?: string;
}

export interface RegisterFormResponse extends RegisterUserApiResponse {
  redirect: string;
}

const lucidBootstrap = async (productUrl: string): Promise<AxiosResponse<LucidBootstrap>> => {
  try {
    const response = await axios({
      method: 'GET',
      url: `${productUrl}/bootstrap`,
    });

    return response as AxiosResponse<LucidBootstrap>;
  } catch (error) {
    throw Error(`Unable to get Lucid Bootstrap data: ${error}`);
  }
};

const registerUser = async ({
  data,
  urlLangPrefix,
  product,
  productUrl,
  checkForService,
}: RegisterUserProps): Promise<RegisterUserResponse> => {
  let isEnterpriseUser = false;
  let redirectUrl = '';
  let registrationStatus = RegistrationStatus.Success;
  try {
    const response = (await axios({
      method: 'POST',
      url: `${productUrl}/${urlLangPrefix}registerForm`,
      data: {
        ...data,
        product: product,
      },
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      // config: { withCredentials: true }
    })) as AxiosResponse<RegisterFormResponse>;
    if (response.status === 200) {
      const {
        data: { redirect },
      } = response;
      redirectUrl = redirect;
      if (redirectUrl.includes('login')) {
        registrationStatus = RegistrationStatus.AlreadyRegistered;
      }

      if (checkForService) {
        const bootstrapResponse = await lucidBootstrap(productUrl);
        const ssoResponse = (await axios({
          method: 'GET',
          url: `${bootstrapResponse.data.services.users}/accounts/sso`,
          params: {
            email: data.email,
            product: product,
          },
        })) as AxiosResponse<{ type: string; url: string }>;
        const ssoPolicy = ssoResponse.data;
        // regular registration or enterprise
        isEnterpriseUser = ssoPolicy.type !== 'password';
        redirectUrl = ssoPolicy.url || `/${urlLangPrefix}users/login`;
      } else {
        redirectUrl = redirect;
      }

      return {
        isEnterpriseUser,
        redirectUrl,
        response,
        registrationStatus,
      };
    }

    return {
      isEnterpriseUser,
      redirectUrl,
      response,
      registrationStatus,
      status: response.status,
      errorMessage: 'Lucid registerForm API returned an unexpected response.',
    };
  } catch (error) {
    throw Error(`Unable to complete User Registration: ${error}`);
  }
};

export default registerUser;
